import { Icon } from 'components/Icon';
import { Spinner } from 'components/Spinner';
import { classNames, React, useLayoutEffect, useMemo, useRef, useState } from 'libs/react';
import { Fragment } from 'react';
import './AutocompleteSelect.scss';
/**
 * @component AutocompleteSelect
 * @since 1.0.0
 * @hidden
 */
export function AutocompleteSelect(props) {
    var _a, _b;
    const { name, options, required, disabled, placeholder, value } = Object.assign({ placeholder: '' }, props);
    const [query, setQuery] = useState('');
    const [editing, setEditing] = useState(false);
    const [open, setOpen] = useState(false);
    const inputRef = useRef(null);
    const overlayContainerRef = useRef(null);
    const ignoreClickRef = useRef(false);
    const selectedLabel = (_b = (_a = options.find((option) => option.value === value)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : '';
    const inputValue = editing ? query : selectedLabel;
    /**
     * @function onBlur
     * @since 1.0.0
     * @hidden
     */
    function onBlur() {
        if (props.onBlur) {
            props.onBlur();
        }
    }
    /**
     * @function onFocus
     * @since 1.0.0
     * @hidden
     */
    function onFocus() {
        if (props.onFocus) {
            props.onFocus();
        }
    }
    /**
     * @function onChange
     * @since 1.0.0
     * @hidden
     */
    function onChange(next) {
        if (props.onChange) {
            props.onChange(next);
        }
        setQuery('');
        setEditing(false);
        setOpen(false);
        onBlur();
    }
    /**
     * @function handleCloseOverlay
     * @since 1.0.0
     * @hidden
     */
    function handleCloseOverlay() {
        const shouldClear = editing && query.trim() === '' && value !== '';
        setOpen(false);
        setEditing(false);
        setQuery('');
        onBlur();
        if (shouldClear && props.onChange) {
            props.onChange('');
        }
    }
    function handleMouseDown() {
        ignoreClickRef.current = !open;
    }
    function handleMouseUp(event) {
        if (!editing) {
            event.preventDefault();
        }
    }
    function handleFocus() {
        onFocus();
        if (!open) {
            setEditing(false);
            setQuery('');
            setOpen(true);
        }
    }
    /**
     * @function handleClick
     * @since 1.0.0
     * @hidden
     */
    function handleClick() {
        if (ignoreClickRef.current) {
            ignoreClickRef.current = false;
            return;
        }
        if (open && !editing) {
            handleCloseOverlay();
        }
    }
    /**
     * @function handleInputChange
     * @since 1.0.0
     * @hidden
     */
    function handleInputChange(event) {
        const next = event.currentTarget.value;
        setEditing(true);
        setQuery(next);
        setOpen(true);
        if (next.trim() === '' && value !== '' && props.onChange) {
            props.onChange('');
        }
    }
    /**
     * @function clearValue
     * @since 1.0.0
     * @hidden
     */
    function clearValue(event) {
        var _a;
        event.preventDefault();
        event.stopPropagation();
        setQuery('');
        setEditing(true);
        setOpen(true);
        if (value !== '' && props.onChange) {
            props.onChange('');
        }
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
    const optionsFormatted = useMemo(() => {
        const needle = (editing ? query : '').trim().toLowerCase();
        if (!needle) {
            return options;
        }
        return options.filter((option) => option.label.toLowerCase().includes(needle));
    }, [options, query, editing]);
    useLayoutEffect(() => {
        var _a;
        if (!open || editing) {
            return;
        }
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.select();
    }, [open, editing]);
    useLayoutEffect(() => {
        if (!open || !value) {
            return;
        }
        const container = overlayContainerRef.current;
        const selected = container === null || container === void 0 ? void 0 : container.querySelector('.autocomplete-select-value-container--selected');
        if (!container || !selected) {
            return;
        }
        const top = selected.offsetTop;
        const bottom = top + selected.offsetHeight;
        if (top < container.scrollTop) {
            container.scrollTop = top;
        }
        else if (bottom > container.scrollTop + container.clientHeight) {
            container.scrollTop = bottom - container.clientHeight;
        }
    }, [open]);
    return (React.createElement("div", { className: classNames('autocomplete-select-container', {
            'autocomplete-select-container--open': open
        }) },
        React.createElement("div", { className: classNames('autocomplete-select-input-container', {
                'autocomplete-select-input-container--has-value': value !== ''
            }) },
            React.createElement("input", { type: "text", name: name, value: inputValue, onMouseDown: handleMouseDown, onMouseUp: handleMouseUp, onClick: handleClick, onFocus: handleFocus, onChange: handleInputChange, onKeyDown: (event) => {
                    if (event.key === 'Escape' && open) {
                        event.preventDefault();
                        event.stopPropagation();
                        setEditing(false);
                        setQuery('');
                        setOpen(false);
                        onBlur();
                    }
                }, ref: inputRef, placeholder: placeholder, required: required, disabled: disabled, autoComplete: "off" }),
            value !== '' && !disabled && (React.createElement("button", { type: "button", className: "autocomplete-select-clear", "aria-label": t('Clear'), onMouseDown: (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }, onClick: clearValue },
                React.createElement(Icon, { value: "fad circle-xmark" })))),
        open && (React.createElement(Fragment, null,
            React.createElement("div", { id: "autocomplete-select-backdrop-overlay", onClick: handleCloseOverlay }),
            React.createElement("div", { className: "autocomplete-select-overlay-container" },
                React.createElement("div", { className: "autocomplete-select-overlay", ref: overlayContainerRef }, !optionsFormatted.length ? (editing && query.trim() ? (React.createElement("div", { className: "autocomplete-select-empty" },
                    React.createElement("span", null, t('No results found')))) : (React.createElement(Spinner, null))) : (optionsFormatted.map((option, index) => (React.createElement("div", { key: `${option.value}-${index}`, className: classNames('autocomplete-select-value-container', {
                        'autocomplete-select-value-container--selected': option.value === value
                    }), onMouseDown: (event) => event.preventDefault(), onClick: () => onChange(option.value) },
                    React.createElement("span", null, option.label)))))))))));
}
